import { request } from 'api/api';
import { API } from 'config/api/apiUrls';
import { deleteFileFromS3Payload, getPresignedUrlPayload } from 'core/common-methods';
import { ButtonType, ContentType, PreSignedUrlPrefix } from 'core/constants';
import { mimeTypes } from 'core/utils/app.constants';
import { useMutation, useQuery } from 'react-query';
interface IpostCountryRetailerConfigurations {
  list: object[];
  country_code: string;
}
interface IOnboardCountry {
  country_code: string[];
  onboarded_status: string;
  is_sfdc_auto_fetch_enabled?: boolean;
}

interface ISequence {
  countryId: string;
  program_type: string;
  status: object;
}

interface IpostRolesAndFeatures {
  countryId: string;
  roles: object[];
}

interface IpostSfdcQueryCatalogues {
  data: object[];
  country_code: string;
}

interface IPropsSFDCToggleForCountry {
  countryData: {
    country_code: string[];
    onboarded_status: string;
    is_sfdc_auto_fetch_enabled?: boolean;
  };
  sequencePayload?: any;
  SfdcQueryCataloguesPayload?: any;
}

export const GetAllCountryList = async (type: string): Promise<IGetAllCountryList> => {
  const res = await request.get(API.super_admin.all_country_list(type));
  const data = res.data as IGetAllCountryList;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};

export const useGetAllCountryList = (type: string, queryProps = {}) =>
  useQuery(['fetchCountryList'], () => GetAllCountryList(type), {
    refetchOnWindowFocus: false,
    ...queryProps,
  });

export const GetAllSfdcQueryCatalogues = async (countryCode: string) => {
  const res = await request.get(API.super_admin.getSfdcQueryCatalogues(countryCode));
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};

export const useGetSfdcQueryCatalogues = (countryCode: string, queryProps = {}) =>
  useQuery(['Sfdc_Query_catalogues'], () => GetAllSfdcQueryCatalogues(countryCode), {
    refetchOnWindowFocus: false,
    ...queryProps,
  });

export const GetCountryRoles = async () => {
  const res = await request.get(API.super_admin.country_roles());
  const data = res.data;
  return { data: data.data, status: res.status, resultCode: data.resultCode };
};
export const OnboardCountry = async (payload: IOnboardCountry) => {
  const res = await request.post(API.super_admin.onboard_country, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const Sequence = async (payload: ISequence) => {
  const res = await request.post(API.super_admin.sequence, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const postSfdcQueryCatalogues = async (payload: IpostSfdcQueryCatalogues) => {
  const res = await request.post(API.super_admin.postSfdcQueryCatalogues, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const postRolesAndFeatures = async (payload: IpostRolesAndFeatures) => {
  const res = await request.post(API.super_admin.rolesAndFeatures, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const putRolesAndFeatures = async (payload: IpostRolesAndFeatures) => {
  const res = await request.post(API.super_admin.rolesAndFeatures, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const getRolesAndFeatures = async (countryId: string) => {
  const res = await request.get(API.super_admin.getRolesAndFeatures(countryId));
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
    key: 'RolesAndFeatures',
  };
};
export const postCountryConfigurations = async ({
  countryId,
  payload,
}: {
  countryId: string;
  payload: any;
}) => {
  const res = await request.post(API.super_admin.countryConfigurations(countryId), payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};
export const getCountryConfigurations = async (countryId: string) => {
  const res = await request.get(API.super_admin.countryConfigurations(countryId));
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
    key: 'countryConfiguration',
  };
};
export const putCountryConfigurations = async ({
  countryId,
  configKey,
  payload,
}: {
  countryId: string;
  configKey: string;
  payload: any;
}) => {
  const res = await request.put(
    API.super_admin.putCountryConfigurations(countryId, configKey),
    payload
  );
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};

export const postCountryRetailerConfigurations = async (
  payload: IpostCountryRetailerConfigurations
) => {
  const res = await request.post(API.super_admin.retailerConfigurations, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};

export const putCountryRetailerConfigurations = async (
  payload: IpostCountryRetailerConfigurations
) => {
  const res = await request.put(API.super_admin.retailerConfigurations, payload);
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
  };
};

export const getCountryRetailerConfigurations = async (countryId: string, type: string) => {
  const res = await request.get(API.super_admin.getRetailerConfigurations(countryId, type));
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    success: data.success,
    key: 'RetailerConfiguration',
  };
};

export const deleteCountry = async (countryId: string) => {
  const res = await request.delete(API.super_admin.delete_country(countryId));
  const data = res.data;
  return {
    data: data.data,
    success: data.success,
    message: data.message,
  };
};

export const useDeleteCountry = () => useMutation(deleteCountry);
export const useSaveRetailerDetails = () =>
  useMutation((props: any) => postCountryRetailerConfigurations(props.retailerList));
export const useEditRetailerDetails = () =>
  useMutation((props: any) => putCountryRetailerConfigurations(props.retailerList));
export const deleteRetailer = async (retailerId: string) => {
  const res = await request.delete(API.updateRetailer(retailerId));
  const data = res.data;
  return {
    data: data.data,
    success: data.success,
    message: data.message,
  };
};
export const useDeleteRetailer = () =>
  useMutation((retailerId: string) => deleteRetailer(retailerId));
export const toggleRetailerStatus = async (retailerId, modifiedData) => {
  const res = await request.put(API.updateRetailer(retailerId), {
    ...modifiedData,
  });
  const data = await res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
    message: data.message,
  };
};

export const searchRetailers = async (countryCode: string, searchText: string) => {
  const res = await request.post(API.retailer_search, {
    countryCode: countryCode,
    name: searchText,
  });
  const data = res.data;
  return {
    data: data.data,
    success: data.success,
    message: data.message,
  };
};

export const useSearchRetailers = () =>
  useMutation(({ countryCode, searchText }: { countryCode: string; searchText: string }) =>
    searchRetailers(countryCode, searchText)
  );

export const useToggleRetailerStatus = () =>
  useMutation((props: any) => toggleRetailerStatus(props.retailerId, props.modifiedData));

export const useSaveCountryDetails = () =>
  useMutation((props: any) => {
    const arrayApi = [
      OnboardCountry(props.countryData),
      postCountryConfigurations(props.unitsAndFormats),
      postRolesAndFeatures(props.rolesAndFeature),
      postCountryRetailerConfigurations(props.retailerList),
      postCountryConfigurations(props.paymentDetails),
      postCountryConfigurations(props.supportDetails),
      postCountryConfigurations(props.faqVideos),
      postCountryConfigurations(props.faqLink),
      postCountryConfigurations(props.campaignSelectionData),
      postCountryConfigurations(props.growerInstructions),
    ];
    props.sequencePayload && arrayApi.push(Sequence(props.sequencePayload));
    props.SfdcQueryCataloguesPayload &&
      arrayApi.push(postSfdcQueryCatalogues(props.SfdcQueryCataloguesPayload));
    return Promise.all(arrayApi);
  });
export const useGetRetailerList = (countryId, type = '', queryProps) =>
  useQuery(['getCountryRetailerConfig'], () => getCountryRetailerConfigurations(countryId, type), {
    cacheTime: 0,
    ...queryProps,
  });

export const useEditSaveCountryDetails = (faqKeyFlag = false, instructionDetailsExists = false) =>
  useMutation((props: any) => {
    const arrayApi = [
      OnboardCountry(props.countryData),
      putCountryConfigurations(props.unitsAndFormats),
      putRolesAndFeatures(props.rolesAndFeature),
      putCountryRetailerConfigurations(props.retailerList),
      putCountryConfigurations(props.paymentDetails),
      putCountryConfigurations(props.supportDetails),
      //existing country doesn't have key for faq sections, so need post api instead of put
      faqKeyFlag
        ? putCountryConfigurations(props.faqVideos)
        : postCountryConfigurations(props.faqVideos),
      faqKeyFlag
        ? putCountryConfigurations(props.faqLink)
        : postCountryConfigurations(props.faqLink),
      instructionDetailsExists
        ? putCountryConfigurations(props.campaignSelectionData)
        : postCountryConfigurations(props.campaignSelectionData),
      instructionDetailsExists
        ? putCountryConfigurations(props.growerInstructions)
        : postCountryConfigurations(props.growerInstructions),
    ];
    props.sequencePayload && arrayApi.push(Sequence(props.sequencePayload));
    props.SfdcQueryCataloguesPayload &&
      arrayApi.push(postSfdcQueryCatalogues(props.SfdcQueryCataloguesPayload));
    return Promise.all(arrayApi);
  });

export const useSFDCToggleForCountry = () =>
  useMutation((props: IPropsSFDCToggleForCountry) => {
    const arrayApi = [OnboardCountry(props.countryData)];
    props.sequencePayload && arrayApi.push(Sequence(props.sequencePayload));
    props.SfdcQueryCataloguesPayload &&
      arrayApi.push(postSfdcQueryCatalogues(props.SfdcQueryCataloguesPayload));
    return Promise.all(arrayApi);
  });

export const useGetCountryConfiguration = (countryId, type = '', queryProps) =>
  useQuery(
    ['getCountryConfig'],
    () =>
      Promise.all([
        getCountryConfigurations(countryId),
        getRolesAndFeatures(countryId),
        getCountryRetailerConfigurations(countryId, type),
      ]),
    {
      cacheTime: 0,
      ...queryProps,
    }
  );

export const postContractsDetails = async (countryId: string, payload: any) => {
  const res = await request.post(`${API.contracts}/${countryId}`, JSON.stringify(payload));
  const data = res.data;
  return {
    data: data.data,
    status: res.status,
    resultCode: data.resultCode,
  };
};

export const getContractDetails = async (countryId: string) => {
  const res = await request.get(`${API.contracts}/${countryId}`);
  const data = await res.data;
  return { data: data.data, status: res.status, resultCode: data.resultCode };
};

export const getPresignedUrl = async (seasonId: string, filename: string, code: string) => {
  const prefix = PreSignedUrlPrefix.SEASON_CONTRACT(seasonId, code);
  const payload = getPresignedUrlPayload(prefix, filename, ContentType.PDF, 'contract');
  const url = `${API.presigned_url}?type=contract`;
  const response = await request.post(url, payload);
  return response.data;
};
export const getPresignedUrlGrower = async (growerId: string, filename: string) => {
  const prefix = `growerBankDetails/${growerId}`;
  const payload = getPresignedUrlPayload(
    prefix,
    filename,
    mimeTypes[filename?.split('.')?.pop()?.toLowerCase()]
  );
  const response = await request.post(API.presigned_url, payload);
  return response.data;
};

export const deleteContractFileFromS3 = async ({
  key,
  seasonId,
  type,
}: {
  key: string;
  seasonId?: string;
  type?: string;
}) => {
  const payload = deleteFileFromS3Payload(key);
  let url = API.delete_file_s3;
  if (seasonId && type) {
    url += `?seasonId=${seasonId}&type=${type}`;
  }
  const res = await request.delete(url, { data: payload });
  if (res.status >= 400) {
    throw new Error(res?.data?.message || 'Unable to delete contract file.');
  }
  return res.data;
};

export const postContractsLinks = async (payload) => {
  const res = await request.post(API.contract_links, payload);
  return res.data;
};

export const getContractDetailsV2 = async (seasonId) => {
  const res = await request.get(`${API.seasons}/${seasonId}/contracts`);
  return res.data;
};

export const getGrowerContractsFromS3 = async (growerId: string, seasonId: string) => {
  const res = await request.get(
    `${API.grower_season_enrollment}${growerId}/contracts/season/${seasonId}`
  );
  return res.data;
};

export const deleteContractLinkDetails = async (
  seasonId: string,
  payload: { code: string; version?: number }
) => {
  const url = `${API.seasons}/${seasonId}/contracts/link?type=contract`;
  const res = await request.delete(url, { data: payload });
  if (res.status >= 400) {
    throw new Error(res?.data?.message || 'Unable to delete contract link.');
  }
  return res.data;
};

export const useGetContractDetails = (countryId: string, queryProps = {}) =>
  useQuery(['getContractDetails'], () => getContractDetails(countryId), {
    ...queryProps,
  });

export const useGetContractDetailsV2 = (seasonId: string, queryProps = {}) =>
  useQuery(['getContractDetailsV2', seasonId], () => getContractDetailsV2(seasonId), {
    ...queryProps,
  });

export const useGetGrowerContractsFromS3 = (growerId: string, seasonId: string, queryProps = {}) =>
  useQuery(
    ['getGrowerContractsFromS3', growerId, seasonId],
    () => getGrowerContractsFromS3(growerId, seasonId),
    {
      ...queryProps,
    }
  );

export const deleteContractDetails = async (countryId: string, payload: any) => {
  const res = await request.delete(`${API.contracts}/${countryId}`, { data: payload });
  const data = res.data;
  return { data: data.data, status: res.status, resultCode: data.resultCode };
};

export interface ICountryData {
  id: string;
  name: string;
  currency: string;
  currency_symbol: string;
  country_url: string;
  is_onboarded: string;
  country_code: string;
  modal_type: string;
  customization: Customization;
  created_by: null;
  created_at: Date;
  updated_at: Date;
  is_deleted: boolean;
  onboarded_status: ButtonType | undefined;
  is_sfdc_auto_fetch_enabled: boolean;
}

export interface Customization {
  retailer_details_info_note: boolean;
}

export interface IGetAllCountryList {
  status: number;
  data: ICountryData[];
  resultCode: string;
  success: string;
}
